import { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { makeAutoObservable } from 'mobx';
import Shape from '../interfaces/Shape';
import GeometryService from '../services/geometryService';

export default class DrawMapStore {
    mode: DrawMapMode = DrawMapMode.None;
    isReadonly: boolean = false;
    result: FeatureCollection<Geometry, GeoJsonProperties> | null = null;
    isResultChanged: boolean = false;
    onOneMoreDealShapeIsDrawn: ((result: FeatureCollection<Geometry, GeoJsonProperties>) => void) | null = null;

    constructor(
        private geometryService: GeometryService
    ) {
        makeAutoObservable(this);
    }

    setIsReadonly(value: boolean) {
        this.isReadonly = value;
    }

    isDrawing() {
        return this.mode !== DrawMapMode.None;
    }

    isActiveDrawing() {
        return this.mode !== DrawMapMode.None && !this.isReadonly;
    }

    drawMeasureArea() {
        if (!this.isDrawControlAvailable())
            return;

        this.mode = DrawMapMode.MeasureArea;
        this.isReadonly = false;
        this.isResultChanged = false;
        this.result = null;
    }

    drawMeasureDistance() {
        if (!this.isDrawControlAvailable())
            return;

        this.mode = DrawMapMode.MeasureDistance;
        this.isReadonly = false;
        this.isResultChanged = false;
        this.result = null;
    }

    drawFocusArea(shape: FeatureCollection<Geometry, GeoJsonProperties> | null = null) {
        if (!this.isDrawControlAvailable())
            return;

        this.mode = DrawMapMode.FocusArea;
        this.isReadonly = false;
        this.isResultChanged = false;
        this.result = shape;
    }

    drawInsights(shape: FeatureCollection<Geometry, GeoJsonProperties> | null = null) {
        this.mode = DrawMapMode.Insights;
        this.isReadonly = !!shape;
        this.isResultChanged = false;
        this.result = shape;
    }

    drawTenantInMarket(shape: Shape) {
        this.mode = DrawMapMode.TenantInMarket;
        this.isReadonly = false;
        this.isResultChanged = false;
        this.result = {
            type: "FeatureCollection",
            features: this.geometryService.mapIShapesToPolygonFeatures([shape]),
        };
    }

    drawNewTenantInMarketShape() {
        this.mode = DrawMapMode.NewTenantInMarketShape;
        this.isReadonly = false;
        this.isResultChanged = false;
        this.result = null;
    }

    drawDeal(shapes: Shape[]) {
        this.mode = DrawMapMode.Deal;
        this.isReadonly = false;
        this.isResultChanged = false;
        this.result = {
            type: "FeatureCollection",
            features: this.geometryService.mapIShapesToPolygonFeatures(shapes),
        };
    }

    drawOneMoreDealShape(onDrawn: (result: FeatureCollection<Geometry, GeoJsonProperties>) => void) {
        this.mode = DrawMapMode.OneMoreDealShape;
        this.onOneMoreDealShapeIsDrawn = onDrawn;
    }

    setDealModeBack() {
        this.mode = DrawMapMode.Deal;
        this.onOneMoreDealShapeIsDrawn = null;
    }

    setResult(value: FeatureCollection<Geometry, GeoJsonProperties>) {
        this.result = value;
        this.isResultChanged = true;
        if (this.mode === DrawMapMode.OneMoreDealShape) {
            this.onOneMoreDealShapeIsDrawn!(value);
            this.setDealModeBack();
        }
    }

    clear() {
        this.mode = DrawMapMode.None;
        this.isReadonly = false;
        this.isResultChanged = false;
        this.result = null;
        this.onOneMoreDealShapeIsDrawn = null;
    }

    isDrawControlAvailable(): boolean {
        // don't draw measure or focus area if anything else is already drawing
        return [DrawMapMode.None, DrawMapMode.MeasureArea, DrawMapMode.MeasureDistance, DrawMapMode.FocusArea].includes(this.mode);
    }
}

export enum DrawMapMode {
    None = "None",
    MeasureArea = "MeasureArea",
    MeasureDistance = "MeasureDistance",
    FocusArea = "FocusArea",
    Insights = "Insights",
    TenantInMarket = "TenantInMarket",
    NewTenantInMarketShape = "NewTenantInMarketShape",
    Deal = "Deal",
    OneMoreDealShape = "OneMoreDealShape",
}